import React, { useEffect, useState, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Box,
  MenuItem,
  Select,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import style from "../../styles/gri/general.module.css";
import { getRowErrorMessages } from "../../utils/getRowErrorMessage";
import {
  validateNumber,
  validateNumberInput,
  validatePercentage,
  validatePercentageInput,
} from "../../utils/validateFields";
import DeleteRowDialog from "./DeleteRowDialog";

const DynamicStaticTable = memo(
  ({
    inputData,
    subHeading,
    tables,
    setTables,
    errors,
    setErrors,
    data,
    defaultSegments,
    parameter,
    buttonHeading,
    disabled,
    hide,
  }) => {
    useEffect(() => {
      setTables(
        inputData?.map((section) => ({
          fieldName: section?.fieldName,
          rows:
            data?.[section?.fieldName]?.length > 0
              ? data[section?.fieldName]
              : defaultSegments.map((el) => ({ [parameter]: el })),
        }))
      );
    }, [data]);

    const [fieldToRemove, setFieldToRemove] = React.useState({
      tableIndex: null,
      rowIndex: null,
    });

    const [openDialog, setOpenDialog] = React.useState(false);
    const [isFilled, setIsFilled] = useState({});

    const handleAddRow = (tableIndex) => {
      setTables((prev) => {
        const newTables = [...prev];
        newTables[tableIndex].rows.push({});
        return newTables;
      });
    };

    const handleRemoveRow = (tableIndex, rowIndex) => {
      setTables((prev) => {
        const newTables = [...prev];
        if (
          Object.entries(newTables[tableIndex].rows[rowIndex]).some(
            ([key, val]) => key !== "_id" && val
          )
        ) {
          setOpenDialog(true);
          setFieldToRemove({ tableIndex, rowIndex });
          return newTables;
        }
        setErrors({});

        newTables[tableIndex].rows.splice(rowIndex, 1);
        return newTables;
      });
    };

    const cancelRemoveRow = () => {
      setOpenDialog(false);
    };

    const confirmRemoveRow = () => {
      if (fieldToRemove) {
        const { tableIndex, rowIndex } = fieldToRemove;
        setTables((prev) => {
          const newTables = [...prev];
          newTables[tableIndex].rows.splice(rowIndex, 1);
          return newTables;
        });
      }
      setErrors({});
      setOpenDialog(false);
    };

    const handleInputChange = (
      tableIndex,
      rowIndex,
      fieldName,
      value,
      type
    ) => {
      if (type === "percentage" && !validatePercentageInput(value)) return;
      if (type === "number" && !validateNumberInput(value)) return;

      const updatedTables = [...tables];
      updatedTables[tableIndex].rows[rowIndex][fieldName] = value;

      const updatedErrors = { ...errors };
      if (updatedErrors[tableIndex] && updatedErrors[tableIndex][rowIndex]) {
        delete updatedErrors[tableIndex][rowIndex][fieldName];
        if (Object.keys(updatedErrors[tableIndex][rowIndex]).length === 0) {
          delete updatedErrors[tableIndex][rowIndex];
        }
        if (Object.keys(updatedErrors[tableIndex]).length === 0) {
          delete updatedErrors[tableIndex];
        }
      }

      setErrors(updatedErrors);
      setTables(updatedTables);
      checkIfAdded();
    };

    useEffect(() => {
      checkIfAdded();
    }, [tables, inputData]); // Run only when tables or inputData change

    const checkIfAdded = () => {
      tables.forEach((table, tableIndex) => {
        const rows = table?.rows || [];
        const lastRow = rows.length > 0 ? rows[rows.length - 1] : {}; // Ensure lastRow is an object
        const filteredLastRow = Object.fromEntries(
          Object.entries(lastRow).filter(([key]) => key !== "_id")
        );

        const optionalLength = inputData?.[tableIndex]?.fields?.filter(
          (el) => el?.optional
        )?.length;
        // Check if lastRow is fully filled
        const isComplete =
          rows?.length === defaultSegments?.length ||
          (Object.keys(filteredLastRow).length >=
            (inputData?.[tableIndex]?.fields?.length - optionalLength || 0) &&
            Object.values(filteredLastRow)?.every(
              (val) =>
                val !== undefined &&
                val !== "" &&
                val !== null &&
                val?.length !== 0
            ));
        setIsFilled((prevState) => ({
          ...prevState,
          [tableIndex]: !isComplete, // true if incomplete, false if fully filled
        }));
      });
    };

    return (
      <Box>
        <DeleteRowDialog
          {...{ openDialog, cancelRemoveRow, confirmRemoveRow }}
        />
        {/* {Heading && <p className={style.Heading}>{Heading}</p>} */}
        {subHeading && <p className={style.subHeading}>{subHeading}</p>}
        {inputData?.map((section, tableIndex) => (
          <div key={section.fieldName} sx={{ marginBottom: "1rem" }}>
            {section?.title && (
              <p
                className={style.ques_heading}
                style={{ textIndent: "-1.2rem", marginLeft: "1.2rem" }}
              >
                {section.title}
              </p>
            )}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                borderRadius: "10px",
                ml: 2,
                width: "98.5%",
              }}
            >
              <Table sx={{ border: "1px solid #ddd", borderRadius: "10px" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#1C486B",
                      border: "1px solid #ddd",
                    }}
                  >
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        width: "1rem",
                        textAlign: "center",
                      }}
                    >
                      S. No.
                    </TableCell>
                    {section?.fields?.map((field) => (
                      <TableCell
                        key={field?.fieldName}
                        style={{
                          border: "1px solid #ddd",
                          fontWeight: "bold",
                          color: "#FFFFFF",
                          width: field.width,
                        }}
                      >
                        {field?.title}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        width: "1rem",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tables[tableIndex]?.rows?.map((row, rowIndex) => {
                    // console.log(row, "checking row");
                    return (
                      <TableRow
                        key={rowIndex}
                        sx={{
                          fontWeight: 400,
                          fontFamily: "inter",
                          fontSize: "14px",
                          lineHeight: "16.94px",
                          "& > th": {
                            color: "white",
                            fontWeight: "bold",
                            py: "0.5rem",
                            px: "0.5rem",
                            border: "1px solid rgba(224, 224, 224, 1)",
                            height: "30px",
                          },
                        }}
                      >
                        <TableCell sx={{ textAlign: "center", width: "8%" }}>
                          {rowIndex + 1}.
                        </TableCell>
                        {section?.fields?.map((field) => (
                          <TableCell
                            key={field?.fieldName}
                            sx={{
                              border: "1px solid #DBDBDB",
                              py: "0.5rem",
                              fontWeight: field.fieldName === parameter && 400,
                              color: field.fieldName === parameter && "#1C486B",
                            }}
                          >
                            {field.fieldName === parameter &&
                            rowIndex < defaultSegments?.length ? (
                              <p
                                style={{
                                  fontWeight: 400,
                                  fontSize: "14px",
                                  lineHeight: "16.94px",
                                  fontFamily: "inter",
                                  marginTop: "1rem",
                                }}
                              >
                                {row[field.fieldName]}
                              </p> // Display the value as plain text if the condition is true
                            ) : field?.type === "select" ? (
                              <Select
                                // disabled={
                                //   rowIndex < defaultSegments?.length &&
                                //   field.fieldName === parameter
                                // }
                                disabled={
                                  disabled ||
                                  (rowIndex < defaultSegments?.length &&
                                    field.fieldName === parameter)
                                }
                                multiple={field?.multiselect}
                                value={
                                  field?.multiselect
                                    ? row[field?.fieldName] || [] // Ensure value is an array for multiselect
                                    : row[field?.fieldName] || "" // Ensure value is a string for single select
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    tableIndex,
                                    rowIndex,
                                    field.fieldName,
                                    e.target.value,
                                    field.type
                                  )
                                }
                                displayEmpty
                                fullWidth
                                size="small"
                                sx={{
                                  border: "none",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      border: "none",
                                    },
                                }}
                                renderValue={(selected) =>
                                  field.multiselect
                                    ? selected.length > 0
                                      ? selected.join(", ")
                                      : field.placeholder || "Select"
                                    : selected || field.placeholder || "Select"
                                }
                              >
                                <MenuItem value={""} disabled>
                                  {field.placeholder || "Select"}
                                </MenuItem>
                                {field.options?.map((option, idx) => (
                                  <MenuItem
                                    key={idx}
                                    value={option?.name || option} // Ensure the `value` matches the `row[field.fieldName]`
                                  >
                                    {field.multiselect ? (
                                      <>
                                        <Checkbox
                                          checked={
                                            row[field.fieldName]?.includes(
                                              option?.name || option
                                            ) || false
                                          } // Check if the option is selected
                                          size="small"
                                        />
                                        {option?.name || option}
                                      </>
                                    ) : (
                                      option?.name || option
                                    )}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : field.type === "radio" ? (
                              // console.log(row,row[field.fieldName],"Checking...")
                              <Box sx={{ display: "flex" }}>
                                <FormControl sx={{ my: 1, display: "flex" }}>
                                  <RadioGroup
                                    row
                                    disabled={
                                      rowIndex <= defaultSegments?.length &&
                                      field.fieldName === parameter
                                    }
                                    value={row[field.fieldName] || "Yes"}
                                    onChange={(e) =>
                                      handleInputChange(
                                        tableIndex,
                                        rowIndex,
                                        field.fieldName,
                                        e.target.value,
                                        field.type
                                      )
                                    }
                                  >
                                    <FormControlLabel
                                      value="Yes"
                                      control={<Radio />}
                                      label="Yes"
                                    />
                                    <FormControlLabel
                                      value="No"
                                      control={<Radio />}
                                      label="No"
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            ) : (
                              <TextField
                                // disabled={
                                //   rowIndex < defaultSegments?.length &&
                                //   field.fieldName === parameter
                                // }
                                disabled={
                                  disabled ||
                                  (rowIndex < defaultSegments?.length &&
                                    field.fieldName === parameter)
                                }
                                placeholder={field.placeholder}
                                type={field.type}
                                value={row[field.fieldName] || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    tableIndex,
                                    rowIndex,
                                    field.fieldName,
                                    e.target.value,
                                    field.type
                                  )
                                }
                                multiline
                                rows={1}
                                sx={{
                                  fontWeight: 400,
                                  color: "#1C486B",
                                  width: "100%",
                                  py: "0.5rem",
                                  fontFamily: "inter",
                                  fontSize: "14px",
                                  lineHeight: "16.94px",
                                  "& .MuiInputBase-root": {
                                    overflowY: "auto",
                                    cursor: "default",
                                  },
                                  "& .MuiInputBase-root:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    color:
                                      field.fieldName === parameter &&
                                      "#1C486B",
                                    "&.Mui-disabled": {
                                      color: "red", // Ensure font color change works for disabled state
                                    },
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                        ))}

                        <TableCell sx={{ width: "8%" }}>
                          <IconButton
                            disabled={
                              rowIndex < defaultSegments?.length || disabled
                            }
                            onClick={() =>
                              handleRemoveRow(tableIndex, rowIndex)
                            }
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, ml: 3 }}>
              {Object.keys(errors).length > 0 &&
                getRowErrorMessages(tableIndex, inputData, tables, errors).map(
                  (errorMessage, idx) => (
                    <p
                      key={idx}
                      color="error"
                      variant="caption"
                      style={{
                        lineHeight: "1.2",
                        margin: "2px 0",
                        color: "#d34040",
                        fontSize: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      {errorMessage}
                    </p>
                  )
                )}
            </Box>
            {/* Add row button */}
            {!hide && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  endIcon={<Add />}
                  disabled={isFilled?.[tableIndex] || disabled}
                  onClick={() => handleAddRow(tableIndex)}
                  sx={{
                    // marginTop: "1rem",
                    color: "#1C486B",
                    width: buttonHeading ? "auto" : "7rem",
                    borderColor: "#1C486B",
                    textTransform: "none",
                  }}
                  variant="outlined"
                >
                  {buttonHeading ? buttonHeading : "Add"}
                </Button>
              </Box>
            )}

            {/* Dynamic error messages for missing fields */}
          </div>
        ))}
      </Box>
    );
  }
);

export default DynamicStaticTable;
